import { useId } from "react";
import { useTranslation } from "react-i18next";
import { PermissionCatalogueEntry } from "types/Permissions";
import {
  groupByModule,
  MASTER_PERMISSION,
  moduleNameKey,
  permissionDescriptionKey,
  permissionNameKey,
  permissionSlug,
} from "./permission-labels";

type PermissionPickerProps = {
  catalogue: PermissionCatalogueEntry[];
  /** The rights given to the person by hand. */
  value: string[];
  /**
   * Rights the person's position already brings. Shown ticked; locked unless
   * the person also has the right by hand, which can then be unticked.
   */
  positionPermissions?: string[];
  /** Without it the Master right is not offered. */
  canAssignMaster: boolean;
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /** Names only, in two columns; the description moves into a tooltip. */
  compact?: boolean;
};

/**
 * Tick boxes for the rights of the fixed list, grouped by module. `value` is
 * only the manual part: a right that comes from the position is ticked and
 * locked, and is never added to `value`. When the person also has it by hand,
 * the box stays enabled: unticking removes only the manual part.
 */
export default function PermissionPicker({
  catalogue,
  value,
  positionPermissions = [],
  canAssignMaster,
  onChange,
  disabled,
  compact,
}: PermissionPickerProps) {
  const { t } = useTranslation();
  const idPrefix = useId();
  const offered = catalogue.filter(
    (right) => right.key !== MASTER_PERMISSION || canAssignMaster
  );

  const toggle = (key: string) =>
    onChange(
      value.includes(key) ? value.filter((held) => held !== key) : [...value, key]
    );

  return (
    <div className="space-y-4">
      {groupByModule(offered).map(({ module, rights }) => (
        <fieldset key={module}>
          <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t(moduleNameKey(module), { defaultValue: module })}
          </legend>
          <div
            className={
              compact
                ? "mt-1 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2"
                : "mt-2 space-y-2.5"
            }
          >
            {rights.map(({ key }) => {
              const fromPosition = positionPermissions.includes(key);
              const byHand = value.includes(key);
              const inputId = `${idPrefix}-${permissionSlug(key)}`;
              const descriptionId = `${inputId}-desc`;
              const description = t(permissionDescriptionKey(key), {
                defaultValue: "",
              });
              return (
                <div key={key} className="flex items-start gap-2">
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={fromPosition || byHand}
                    disabled={disabled || (fromPosition && !byHand)}
                    onChange={() => toggle(key)}
                    aria-describedby={compact ? undefined : descriptionId}
                    className="mt-0.5 size-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="min-w-0">
                    <label
                      htmlFor={inputId}
                      title={compact ? description : undefined}
                      className="text-sm font-medium text-gray-900"
                    >
                      {t(permissionNameKey(key), { defaultValue: key })}
                    </label>
                    {fromPosition && (
                      <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                        {t("perm_from_position")}
                      </span>
                    )}
                    {!compact && (
                      <p id={descriptionId} className="text-sm text-gray-600">
                        {description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
